import { callRpc, createRpcError } from './base';
import { ensureArray, ensureObject } from './util';
import { coerceBoolean } from '../../utils/coerceBoolean';
import type {
  ProgramLockActionResponse,
  ProgramLockMetadata,
  ProgramLockStatus,
  ProgramBulkLockResult,
  ProgramLockErrorDetail
} from '../../types/rpc-contracts';

const normalizeMetadata = (metadata: unknown): ProgramLockMetadata => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return {};
  }
  return metadata as ProgramLockMetadata;
};

const normalizeLockStatus = (payload: unknown, context: Record<string, unknown>): ProgramLockStatus => {
  const status = ensureObject<ProgramLockStatus>(
    payload,
    'INVALID_LOCK_STATUS',
    'Program lock RPC returned invalid lock status payload',
    context
  );

  return {
    program_id: status.program_id,
    data_locked: coerceBoolean(status.data_locked),
    locked_by: status.locked_by ?? null,
    locked_at: status.locked_at ?? null,
    lock_reason: status.lock_reason ?? null,
    lock_metadata: normalizeMetadata(status.lock_metadata)
  };
};

const normalizeLockResponse = (
  payload: unknown,
  errorDetails: Record<string, unknown>
): ProgramLockActionResponse => {
  const envelope = ensureObject<ProgramLockActionResponse & Record<string, unknown>>(
    payload,
    'INVALID_LOCK_RESPONSE',
    'Program lock RPC returned invalid response envelope',
    errorDetails
  );

  const success = Boolean(envelope.success);
  const error = typeof envelope.error === 'string' ? envelope.error : undefined;
  const data = 'data' in envelope && envelope.data
    ? normalizeLockStatus(envelope.data, errorDetails)
    : undefined;

  return { success, data, error };
};

const transformBulkErrors = (payload: unknown): ProgramLockErrorDetail[] => {
  if (!payload) return [];
  if (!Array.isArray(payload)) {
    throw createRpcError(
      'INVALID_LOCK_ERRORS',
      'Bulk lock RPC errors payload is not an array',
      { payload }
    );
  }

  return payload.map((entry) => {
    const normalized = ensureObject<ProgramLockErrorDetail & Record<string, unknown>>(
      entry,
      'INVALID_LOCK_ERROR_ENTRY',
      'Bulk lock RPC returned invalid error entry',
      { entry }
    );

    if (typeof normalized.program_id !== 'string') {
      throw createRpcError(
        'INVALID_LOCK_ERROR_ENTRY',
        'Bulk lock error entry missing program_id',
        { entry }
      );
    }

    return {
      program_id: normalized.program_id,
      error: typeof normalized.error === 'string' ? normalized.error : 'Unknown error'
    };
  });
};

export const lockProgramData = async (
  programId: string,
  reason: string,
  metadata: ProgramLockMetadata = {},
  operatorId?: string | null
): Promise<ProgramLockActionResponse> => {
  return await callRpc('lock_program_data', {
    params: {
      p_program_id: programId,
      p_reason: reason,
      p_metadata: metadata,
      p_operator_id: operatorId ?? null
    },
    transform: (data) => normalizeLockResponse(data, { programId, reason, metadata })
  });
};

export const unlockProgramData = async (
  programId: string,
  unlockReason: string,
  operatorId?: string | null,
  metadata: ProgramLockMetadata = {}
): Promise<ProgramLockActionResponse> => {
  return await callRpc('unlock_program_data', {
    params: {
      p_program_id: programId,
      p_unlock_reason: unlockReason,
      p_operator_id: operatorId ?? null,
      p_metadata: metadata
    },
    transform: (data) => normalizeLockResponse(data, { programId, unlockReason, metadata })
  });
};

export const getProgramLockStatus = async (
  programId: string
): Promise<ProgramLockStatus> => {
  return await callRpc('get_program_lock_status', {
    params: { p_program_id: programId },
    transform: (data) => normalizeLockStatus(data, { programId })
  });
};

export const bulkLockPrograms = async (
  programIds: string[],
  reason: string,
  metadata: ProgramLockMetadata = {},
  operatorId?: string | null
): Promise<ProgramBulkLockResult> => {
  if (!Array.isArray(programIds) || programIds.length === 0) {
    throw createRpcError('INVALID_LOCK_REQUEST', 'At least one program id is required', {
      programIds
    });
  }

  return await callRpc('bulk_lock_programs', {
    params: {
      p_program_ids: programIds,
      p_reason: reason,
      p_metadata: metadata,
      p_operator_id: operatorId ?? null
    },
    transform: (data) => {
      const envelope = ensureObject<ProgramBulkLockResult & Record<string, unknown>>(
        data,
        'INVALID_BULK_LOCK_RESPONSE',
        'Bulk lock RPC returned invalid response envelope',
        { data, programIds }
      );

      const resultsPayload = 'results' in envelope ? envelope.results : [];
      const transformedResults = ensureArray<unknown>(
        resultsPayload,
        'INVALID_BULK_LOCK_RESULTS',
        'Bulk lock RPC returned invalid results payload',
        { resultsPayload }
      ).map((entry) => normalizeLockResponse(entry, { programIds, reason, metadata }));

      const errors = transformBulkErrors('errors' in envelope ? envelope.errors : []);

      return {
        success: Boolean(envelope.success),
        locked_count: typeof envelope.locked_count === 'number' ? envelope.locked_count : transformedResults.length,
        results: transformedResults,
        errors
      } satisfies ProgramBulkLockResult;
    }
  });
};

export const getLockedProgramsReport = async (): Promise<ProgramLockStatus[]> => {
  return await callRpc('get_locked_programs_report', {
    transform: (data) =>
      ensureArray<ProgramLockStatus>(
        data,
        'INVALID_LOCKED_PROGRAMS_RESPONSE',
        'Locked programs report returned invalid payload',
        { data }
      ).map((row) => normalizeLockStatus(row, { report: 'locked_programs' }))
  });
};
